import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Device, SendingStatus} from '../model/device';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {Filter, Page} from '../model/page';
import {Account} from '../model/account';
import {AuthService} from './auth.service';
import {Configuration} from '../model/configuration';
import {HttpParameterCodec} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private static handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was:`, error.error);
    }
    return throwError(
      'Something bad happened; please try again later.');
  }

  constructor(@Inject('environment') private environment, private http: HttpClient, private authService: AuthService) {
  }

  devices(limit: number, page: number, filters: Filter[]): Observable<Page<Device>> {
    let params = new HttpParams({encoder: new HttpUrlEncodingCodec()});
    filters.forEach(element => {
      params = params.append(element.criteriaKey, element.value);
    });
    params = params.set('limit', limit.toString());
    params = params.set('page', page.toString());
    params = params.append('deviceTypeFamily', 'BRIO');
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices`,
      {
        params
      })
      .pipe(
        map(res => new Page(Device, res)),
        catchError(ApiService.handleError)
      );
  }

  comstatus(ids: string[]): Observable<{ id: string, status: SendingStatus, lastCallDate: Date, lastReadingRequestDate: Date, lastConfigurationSendingDate: Date }[]> {
    return this.http.post<{ id: string, status: number, lastCallDate: Date, lastReadingRequestDate: Date, lastConfigurationSendingDate: Date }[]>(
      `${this.environment.apiUrl}/api/v1/devices/comstatus`,
      ids)
      .pipe(
        map(res => res.map(elm => ({
            id: elm.id,
            lastCallDate: elm.lastCallDate,
            lastReadingRequestDate: elm.lastReadingRequestDate,
            lastConfigurationSendingDate: elm.lastConfigurationSendingDate,
            status: (_ => {
              switch (elm?.status) {
                case 0:
                  return SendingStatus.Unknown;
                case 1:
                  return SendingStatus.Sending;
                case 2:
                  return SendingStatus.Sent;
                case 3:
                  return SendingStatus.Ack;
                case 4:
                  return SendingStatus.Error;
              }
            })()
          })),
          catchError(ApiService.handleError),
        )
      );
  }

  device(id: string): Observable<Device> {
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices/${id}`)
      .pipe(
        map(res => new Device(res)),
        catchError(ApiService.handleError),
      );
  }

  requestConfiguration(id: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/${id}/requestconfig`)
      .pipe(
        catchError(ApiService.handleError),
      );
  }

  configuration(id: string, configuration?: any): Observable<Configuration|boolean> {
    if (!configuration) {
      return this.http.get(
        `${this.environment.apiUrl}/api/v1/devices/${id}/configuration`)
        .pipe(
          map(res => res ? new Configuration(res) : null),
          catchError(ApiService.handleError),
        );
    } else {
      return this.http.post<boolean>(
        `${this.environment.apiUrl}/api/v1/devices/${id}/configuration`,
        configuration)
        .pipe(catchError(ApiService.handleError));
    }
  }

  account(): Observable<Account> {
    return this.http.get(`${this.environment.apiUrl}/api/v1/account`)
      .pipe(
        map(res => new Account(res)),
        tap(account => this.authService.accountChanged.emit(account)),
        catchError(ApiService.handleError),
      );
  }
}


export class HttpUrlEncodingCodec implements HttpParameterCodec {
    encodeKey(k: string): string { return standardEncoding(k); }
    encodeValue(v: string): string { return standardEncoding(v); }
    decodeKey(k: string): string { return decodeURIComponent(k); }
    decodeValue(v: string) { return decodeURIComponent(v); }
}
function standardEncoding(v: string): string {
    return encodeURIComponent(v);
}
